import { Search } from "../search";
import { FlightSearch } from "./search";
import i18n from '../../../plugins/i18n';

export namespace FlightFilter {

  export enum SortBy {
    Duration = 0,
    Price = 1,
    DepartureTime = 2,
    ArrivalTime = 3,
    StopsDuration = 4,
  }

  export class Filter {
    stops: Stop[];
    priceRange: PriceRange;
    carriers: Carrier[];
    sortBy: SortBy;
    airports: Airport[];
    
    constructor(stops: Stop[], priceRange: PriceRange, carriers: Carrier[], sortBy: SortBy, airports: Airport[]) {
      this.stops = stops;
      this.priceRange = priceRange;
      this.carriers = carriers;
      this.sortBy = sortBy;
      this.airports = airports
    }

    filterOnDefaults = (): boolean => {
      return (this.stops.nothingSelected() || this.stops.find(it => it.count === -1).selected)
        && this.carriers.nothingSelected()
        && this.priceRange.right === this.priceRange.max
        && this.priceRange.left === this.priceRange.min;
    }

    static compose = (itineraries: FlightSearch.Itineraries[], prev?: Filter): Filter => {
      return new Filter(
        Stop.compose(itineraries, prev?.stops),
        PriceRange.compose(itineraries, prev?.priceRange),
        Carrier.compose(itineraries, prev?.carriers),
        prev ? prev.sortBy : SortBy.Price,
        Airport.compose(itineraries, prev?.airports),
      );
    }

    static sortingOptions = (): { title: string}[] => {
      return Object.values(SortBy)
        .filter(it => typeof it === 'number')
        .map(it => {
          switch (it) {
            case SortBy.Price : return String(i18n.global.t('flights.sortByOptions.price'));
            case SortBy.Duration: return String(i18n.global.t('flights.sortByOptions.duration'));
            case SortBy.DepartureTime : return String(i18n.global.t('flights.sortByOptions.departureTime'));
            case SortBy.ArrivalTime : return String(i18n.global.t('flights.sortByOptions.arrivalTime'));
            case SortBy.StopsDuration : return String(i18n.global.t('flights.sortByOptions.stopsDuration'));
            default : return String(i18n.global.t('flights.sortByOptions.any'));
          }
        }).map(it => { return {title: it} })
    }

    airportsOptions = (): {
      parent: FlightSearch.Place,
      airports: Airport[],
    }[] => {
      return [...this.airports.groupBy(it => it.place.parent.id)].map(it => {
        return {
          parent: it[1][0].place.parent,
          airports: it[1]
        }
      }).filter(it => it.airports.length > 1);

    }
  } 

  export class Selectable {
    selected: boolean = false;
    disabled?: boolean;
    constructor(selected: boolean) { this.selected = selected; }
    toggle() { this.selected = !this.selected; }
  }


  export class Stop extends Selectable {
    count: number;
    title: string;
 
    constructor(count: number, title: string, selected: boolean) {
      super(selected);
      this.count = count;
      this.title = title;
    }
    
    
    static compose = (itineraries: FlightSearch.Itineraries[], oldStops: Stop[]): Stop[] => {

      const oldAny = oldStops?.find(x => x.count === -1);
      const stops: Stop[] = [oldAny ? oldAny : new Stop(-1, String(i18n.global.t('flights.sortByOptions.any')), true)];
      itineraries.forEach(it => {
        [it.outboundLeg, it.inboundLeg].filter(it => it).forEach(it => {
          const count = it.stations.stops.length;
          if (stops.findIndex(x => x.count === count) < 0) {
            const oldStop = oldStops?.find(x => x.count === count);
            stops.push(oldStop ? oldStop : new Stop(
              count, 
              String(i18n.global.t('flights.offer.stops', count)),
              false,
            ));
          }
        });
      });

      return stops.sortBy(it => it.count);
    }
  }

  export class Airport extends Selectable {
    name: string;
    place: FlightSearch.Place;

    constructor(place: FlightSearch.Place) {
      super(false);
      this.place = place;
      this.name = place.name;
    }

    static compose = (itineraries: FlightSearch.Itineraries[], prevFilters?: Airport[]): Airport[] => {

      const airports = (x: FlightSearch.Itineraries): FlightSearch.Place[] => {
        const a = [x.outboundLeg.stations.destination, x.outboundLeg.stations.origin];
        if (x.inboundLeg) {
          a.push(x.inboundLeg.stations.destination);
          a.push(x.inboundLeg.stations.origin);
        }
        return a.distinct(it => it.id);
      } 

      return itineraries
        .flatMap(it => airports(it))
        .distinct(it => it.id)
        .filter(it => it.parent)
        .map(it => {
          const prev = prevFilters?.find(x => x.place.id === it.id);
          return prev ? prev : new Airport(it);
        });
    }

  }

  export enum FilterKey {
    Carrier = 'carrier',
    Stops = 'stops'
  }

  export class Carrier extends Selectable {
    id: string;
    name: string;

    constructor(id: string, name: string) {
      super(false);
      this.id = id;
      this.name = name;
    }

    static compose = (itineraries: FlightSearch.Itineraries[], oldCarriers?: Carrier[]) => {
      const carriers: Carrier[] = [];
      itineraries.forEach(it => {
        [it.outboundLeg, it.inboundLeg]
          .filter(it => it)
          .forEach(it => {
            it.carriers.forEach(it => {
              if (carriers.findIndex(x => x.id === it.id) < 0) {
                const oldCarrier = oldCarriers?.find(x => x.id === it.id);
                carriers.push(oldCarrier ? oldCarrier : new Carrier(it.id, it.name));
              }
            });
          });
      });
      return carriers;
    }
  }


  export class PriceRange {
    min: number;
    max: number;
    left: number;
    right: number;

    constructor(min: number, max: number, left: number, right: number) {
      this.min = parseFloat(min.toFixed(2));
      this.max = parseFloat(max.toFixed(2));
      this.left = parseFloat(left.toFixed(2));
      this.right = parseFloat(right.toFixed(2));
    }
    
    static compose = (itineraries: FlightSearch.Itineraries[], oldPriceRange?: PriceRange): PriceRange => {
      const prices: number[] = itineraries.map(it => it.prices[0].totalPrice);
      if (prices.length === 0) {
        return new PriceRange(0, 0, 0, 0);
      }
      const min = prices.min();
      const max = prices.max();
      if (oldPriceRange && oldPriceRange.applied()) {
        const left = oldPriceRange.left >= min && oldPriceRange.left < max ? oldPriceRange.left : min;
        const right = oldPriceRange.right > min && oldPriceRange.right <= max ? oldPriceRange.right : max;
        return new PriceRange(min, max, left, right);
      } else {
        return new PriceRange(min, max, min, max);
      }
    }

    applied = (): boolean => {
      return this.left != this.min || this.max != this.right;
    }

  }

}